/**
 * Dashboard Map Layer — 모듈 폴리곤
 *
 * WFS 대신 사이트 설계 API로 OL VectorLayer를 직접 구축.
 * 모든 피처에 solar_module_seq, solar_string_seq, solar_array_seq 보장.
 *
 * 사용:
 *   const layer = await buildModulePolygonLayer({ siteId, style });
 *   map.addLayer(layer);
 */
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Polygon } from 'ol/geom';
import Feature from 'ol/Feature';
import siteDesignApi from '@/apis/panel/site_design_api';

/**
 * siteDesign JSON → Array → String → Module 계층 (array.id = PK, arrayId = 표시값)
 * @param {object} siteDesign
 * @returns {object[]}
 */
export function parseSiteDesignModuleHierarchy(siteDesign) {
  const arrays = [];
  for (const section of siteDesign?.solarSections || []) {
    for (const array of section.solarArrays || []) {
      const strings = (array.solarStrings || []).map(str => ({
        seq: str.id,
        displayId: str.stringId,
        modules: (str.solarModules || []).map(mod => ({
          seq: mod.id,
          displayId: mod.moduleId
        }))
      }));
      arrays.push({
        seq: array.id,
        displayId: array.arrayId,
        strings
      });
    }
  }
  arrays.sort((a, b) => {
    const aid = a.displayId != null ? Number(a.displayId) : NaN;
    const bid = b.displayId != null ? Number(b.displayId) : NaN;
    if (!Number.isNaN(aid) && !Number.isNaN(bid)) return aid - bid;
    if (!Number.isNaN(aid)) return -1;
    if (!Number.isNaN(bid)) return 1;
    return String(a.displayId ?? '').localeCompare(String(b.displayId ?? ''));
  });
  return arrays;
}

function buildModuleFeaturesFromSiteDesign(siteDesign) {
  const features = [];
  for (const section of siteDesign?.solarSections || []) {
    for (const array of section.solarArrays || []) {
      const solarArraySeq = array.id;
      const arrayId = array.arrayId;
      for (const str of array.solarStrings || []) {
        const solarStringSeq = str.id;
        const stringId = str.stringId;
        for (const mod of str.solarModules || []) {
          const solarModuleSeq = mod.id;
          const moduleId = mod.moduleId;
          const coords = mod?.shapeGeog?.coordinates;
          if (!coords?.length) continue;
          const ring = _normalizeRing(coords);
          if (!ring || ring.length < 4) continue;
          const feature = new Feature({
            geometry: new Polygon([ring]),
            solar_module_seq: solarModuleSeq,
            solar_string_seq: solarStringSeq,
            solar_array_seq: solarArraySeq,
            module_id: moduleId,
            string_id: stringId,
            array_id: arrayId
          });
          feature.setId(`solar_module_${solarModuleSeq}`);
          features.push(feature);
        }
      }
    }
  }
  return features;
}

/**
 * 사이트 설계 JSON → OL VectorLayer (모듈 폴리곤)
 *
 * @param {object} opts
 * @param {string|number} opts.siteId
 * @param {import('ol/style/Style').StyleFunction} opts.style — OL 스타일 함수
 * @returns {Promise<{ layer: import('ol/layer/Vector').default|null, hierarchy: object[], features: import('ol/Feature').default[] }>}
 */
export async function buildModulePolygonLayer({ siteId, style }) {
  if (!siteId) {
    console.warn('[dashboardMap] buildModulePolygonLayer: siteId 없음');
    return { layer: null, hierarchy: [], features: [] };
  }

  const siteDesign = await siteDesignApi.getSiteDesign(siteId);
  if (!siteDesign?.solarSections?.length) {
    console.warn('[dashboardMap] siteDesign 데이터 없음');
    return { layer: null, hierarchy: [], features: [] };
  }

  const hierarchy = parseSiteDesignModuleHierarchy(siteDesign);
  const features = buildModuleFeaturesFromSiteDesign(siteDesign);

  console.log('[dashboardMap] modulePolygonLayer built:', features.length, 'features');

  const source = new VectorSource({ features, wrapX: false });
  const heavyLayer = features.length > 200;

  const layer = new VectorLayer({
    source,
    style,
    zIndex: 25,
    renderMode: heavyLayer ? 'image' : 'vector',
    updateWhileAnimating: !heavyLayer,
    updateWhileInteracting: !heavyLayer,
    properties: { layerKind: 'solar_module' }
  });

  return { layer, hierarchy, features };
}

/**
 * GeoJSON coordinates 정규화 → 단일 링 배열 [[lng,lat], ...]
 */
function _normalizeRing(coords) {
  if (!coords?.length) return null;
  const first = coords[0];
  // 중첩 배열: [[[x,y],...]]
  if (Array.isArray(first) && Array.isArray(first[0]) && typeof first[0][0] === 'number') {
    return first;
  }
  // 단일 링: [[x,y],...]
  if (Array.isArray(first) && typeof first[0] === 'number') {
    return coords;
  }
  // 중첩 배열의 첫 번째 링
  if (Array.isArray(first) && Array.isArray(first[0]) && Array.isArray(first[0][0])) {
    return first[0];
  }
  return null;
}
